/**
 * fleet-failure.ts
 * add-locx-fleet-failure-reports PR1 — contracts for the fleet failure report
 * ("informe") + its N fallas.
 *
 * Structural clone of contracts-rrhh's recruitment_request/candidacy pair
 * (add-nomi-candidate-pipeline): fleet_failure_report is the "informe" filed
 * against an asset; N fleet_failure rows hang off it, each with its OWN
 * lifecycle, independent of the others and of the report's own lifecycle.
 *
 * importance/maintenanceKind are CLOSED ZOD ENUMS, NOT identity.state — they
 * are descriptive attributes with no transitions, never confuse with
 * currentState (the real FSM source of truth for both entities).
 */
import { z } from 'zod';
import { CapturedAtSchema, ClientRequestIdSchema } from '@suite/contracts-kernel';

// ---------------------------------------------------------------------------
// Closed vocabularies — descriptive attributes, NOT workflows (no FSM, no
// identity.state row). See servicios/CLAUDE.md: fixed, non-tenant-varying
// vocabulary is code, not config (red line #1/#4).
// ---------------------------------------------------------------------------

export const FAILURE_IMPORTANCE = ['low', 'normal', 'high', 'urgent'] as const;
export const ImportanceSchema = z.enum(FAILURE_IMPORTANCE, {
  errorMap: () => ({ message: 'importance must be one of: low, normal, high, urgent' }),
});
export type Importance = z.infer<typeof ImportanceSchema>;

export const MAINTENANCE_KIND = ['corrective', 'preventive'] as const;
export const MaintenanceKindSchema = z.enum(MAINTENANCE_KIND, {
  errorMap: () => ({ message: 'maintenanceKind must be one of: corrective, preventive' }),
});
export type MaintenanceKind = z.infer<typeof MaintenanceKindSchema>;

// ---------------------------------------------------------------------------
// fleet_failure_report — the "informe" (design.md §2)
// ---------------------------------------------------------------------------

export const FLEET_FAILURE_REPORT_STATES = ['ABIERTO', 'CERRADO'] as const;
export type FleetFailureReportFsmState = (typeof FLEET_FAILURE_REPORT_STATES)[number];

export const FleetFailureReportCreateSchema = z.object({
  assetId: z.string().uuid(),
  costCenterId: z.string().uuid().optional(),
  // Nullable numeric — NOT forced to a fabricated 0 when the asset/report has
  // no meter reading (a legacy vice this change deliberately does not repeat).
  odometerHours: z.coerce.number().nonnegative('odometerHours must be ≥ 0').optional(),
  odometerKm: z.coerce.number().nonnegative('odometerKm must be ≥ 0').optional(),
  notes: z.string().optional(),
  // reportedByUserId NEVER here — from TenantContext (KC-3.5).
  // reportedAt defaults to the server time; operator-offline-forms lets the
  // field app send when the form was filled (capturedAt, bounded to 72 h)
  // plus an idempotency key. Both optional (S4).
  clientRequestId: ClientRequestIdSchema.optional(),
  capturedAt: CapturedAtSchema.optional(),
});
export type FleetFailureReportCreate = z.infer<typeof FleetFailureReportCreateSchema>;

/**
 * Edits core fields of an already-filed informe. Deliberately excludes
 * assetId (immutable identity of what the report is about) and
 * reportedByUserId/currentStateId (owned by TenantContext / the FSM
 * transition route respectively) — same posture as
 * RecruitmentRequestUpdateSchema excluding requestedByUserId/currentStateId.
 */
export const FleetFailureReportUpdateSchema = z.object({
  costCenterId: z.string().uuid().nullable().optional(),
  odometerHours: z.coerce.number().nonnegative('odometerHours must be ≥ 0').nullable().optional(),
  odometerKm: z.coerce.number().nonnegative('odometerKm must be ≥ 0').nullable().optional(),
  notes: z.string().nullable().optional(),
});
export type FleetFailureReportUpdate = z.infer<typeof FleetFailureReportUpdateSchema>;

export const FleetFailureReportTransitionSchema = z.object({
  // ABIERTO is never a transition target — only non-initial states are valid inputs.
  toState: z.enum(['CERRADO'], {
    errorMap: () => ({ message: 'toState must be CERRADO' }),
  }),
});
export type FleetFailureReportTransition = z.infer<typeof FleetFailureReportTransitionSchema>;

export const FleetFailureReportResponseSchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  assetId: z.string().uuid(),
  costCenterId: z.string().uuid().nullable(),
  // Nullable since migration 0108 (fleet-qr-compliance PR2) — null when
  // source='qr_checklist' (no authenticated user in that flow).
  reportedByUserId: z.string().uuid().nullable(),
  odometerHours: z.string().nullable(),
  odometerKm: z.string().nullable(),
  reportedAt: z.string().datetime().or(z.date()),
  currentStateId: z.string().uuid(),
  currentState: z.enum(FLEET_FAILURE_REPORT_STATES),
  notes: z.string().nullable(),
  // fleet-qr-compliance PR2 (design.md §3.4) — provenance of the report.
  // 'voz' added by locx-voz-multi-intencion (D11) — no DB CHECK, verified:
  // migration 0108 left fleet_failure_report.source unconstrained.
  source: z.enum(['manual', 'qr_checklist', 'voz']),
  reportedByName: z.string().nullable(),
  createdAt: z.string().datetime().or(z.date()),
  updatedAt: z.string().datetime().or(z.date()),
  // Denormalized display fields — populated by GET /servicios/fleet-failure-reports
  // (list) and GET .../:id (detail) so the UI can show a vehicle
  // label / cost center name / reporter name without a round-trip per row (same
  // posture as FleetFailureResponse's assetSerialNumber below). Absent
  // (undefined) on create/update responses.
  assetSerialNumber: z.string().optional(),
  assetDescription: z.string().nullable().optional(),
  costCenterName: z.string().nullable().optional(),
  reportedByUserName: z.string().nullable().optional(),
});
export type FleetFailureReportResponse = z.infer<typeof FleetFailureReportResponseSchema>;

export const FleetFailureReportDetailResponseSchema = FleetFailureReportResponseSchema.extend({
  availableTransitions: z.array(FleetFailureReportTransitionSchema.shape.toState),
});
export type FleetFailureReportDetailResponse = z.infer<typeof FleetFailureReportDetailResponseSchema>;

// ---------------------------------------------------------------------------
// fleet_failure_attachment (design.md §4) — ONE ROW PER FALLA, not per
// informe (a deliberate improvement over the legacy system, which attached
// photos at the informe level only).
// ---------------------------------------------------------------------------

export const FleetFailureAttachmentResponseSchema = z.object({
  id: z.string().uuid(),
  fileId: z.string().uuid(),
  originalName: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number(),
  createdAt: z.string().datetime().or(z.date()),
});
export type FleetFailureAttachmentResponse = z.infer<typeof FleetFailureAttachmentResponseSchema>;

export const AttachFleetFailurePhotosSchema = z
  .object({
    fileIds: z.array(z.string().uuid()).min(1, 'At least one fileId is required'),
  })
  .strict();
export type AttachFleetFailurePhotosInput = z.infer<typeof AttachFleetFailurePhotosSchema>;

// ---------------------------------------------------------------------------
// fleet_failure — ONE falla within a fleet_failure_report (design.md §3/§4)
// ---------------------------------------------------------------------------

export const FLEET_FAILURE_STATES = ['EMITIDO', 'ASIGNADO', 'ESPERA_REPUESTO', 'RESUELTO'] as const;
export type FleetFailureFsmState = (typeof FLEET_FAILURE_STATES)[number];

/**
 * assignedEmploymentId (interno) and externalProviderId (externo) are
 * mutually exclusive — enforced here as defense-in-depth (immediate 422 body
 * validation) AND re-validated by CreateFleetFailureUseCase before touching
 * the DB (design.md §5/§8) AND by a CHECK constraint at the DB layer
 * (migration 0098) — three independent layers, same posture the legacy PUT
 * lacked entirely.
 */
export const FleetFailureCreateSchema = z
  .object({
    reportId: z.string().uuid(),
    description: z.string().min(1),
    failureTypeId: z.string().uuid().optional(),
    importance: ImportanceSchema.default('normal'),
    maintenanceKind: MaintenanceKindSchema.optional(),
    assignedEmploymentId: z.string().uuid().optional(),
    externalProviderId: z.string().uuid().optional(),
    laborHours: z.coerce.number().nonnegative('laborHours must be ≥ 0').optional(),
    notes: z.string().optional(),
    // odometerHours/odometerKm (MR-4, locx-paridad-operativa-bramix Slice C) —
    // per-falla meter readings, COMPLEMENTING (not replacing)
    // fleet_failure_report.odometerHours/odometerKm. Nullable numeric — NOT
    // forced to a fabricated 0 when absent (same posture as
    // FleetFailureReportCreateSchema above).
    odometerHours: z.coerce.number().nonnegative('odometerHours must be ≥ 0').optional(),
    odometerKm: z.coerce.number().nonnegative('odometerKm must be ≥ 0').optional(),
    // meterReadingMissingReason (MR-5.2/D3) — justification for a missing
    // REQUIRED reading. Create-only (never on FleetFailureUpdateSchema) so the
    // "pending workshop review" marker can never go stale. Same shape as
    // fuel-load.ts's field of the same name.
    meterReadingMissingReason: z.string().trim().min(1).max(500).optional(),
    // operator-offline-forms — idempotency key for a resend from the field app (S3).
    clientRequestId: ClientRequestIdSchema.optional(),
    // companyId, currentStateId NEVER here — from context/server.
  })
  .superRefine((data, ctx) => {
    if (data.assignedEmploymentId && data.externalProviderId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['externalProviderId'],
        message: 'assignedEmploymentId and externalProviderId are mutually exclusive — set at most one.',
      });
    }
  });
export type FleetFailureCreate = z.infer<typeof FleetFailureCreateSchema>;

/**
 * Edits descriptive fields of an already-filed falla. Deliberately EXCLUDES
 * assignedEmploymentId/externalProviderId — reassignment is a bigger
 * operation than a generic field edit (it would need the SAME
 * IEmployeeLookup/exclusivity validation CreateFleetFailureUseCase performs,
 * and no dedicated reassignment use-case is scoped for this PR — see the
 * final report's design-decisions section). reportId/currentStateId are
 * likewise excluded (immutable identity / owned by the FSM transition route).
 */
export const FleetFailureUpdateSchema = z.object({
  description: z.string().min(1).optional(),
  failureTypeId: z.string().uuid().nullable().optional(),
  importance: ImportanceSchema.optional(),
  maintenanceKind: MaintenanceKindSchema.nullable().optional(),
  laborHours: z.coerce.number().nonnegative('laborHours must be ≥ 0').nullable().optional(),
  notes: z.string().nullable().optional(),
});
export type FleetFailureUpdate = z.infer<typeof FleetFailureUpdateSchema>;

export const FleetFailureTransitionSchema = z.object({
  // EMITIDO is never a transition target — only non-initial states are valid inputs.
  toState: z.enum(['ASIGNADO', 'ESPERA_REPUESTO', 'RESUELTO'], {
    errorMap: () => ({ message: 'toState must be one of: ASIGNADO, ESPERA_REPUESTO, RESUELTO' }),
  }),
});
export type FleetFailureTransition = z.infer<typeof FleetFailureTransitionSchema>;

export const FleetFailureResponseSchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  reportId: z.string().uuid(),
  description: z.string(),
  failureTypeId: z.string().uuid().nullable(),
  importance: ImportanceSchema,
  maintenanceKind: MaintenanceKindSchema.nullable(),
  assignedEmploymentId: z.string().uuid().nullable(),
  externalProviderId: z.string().uuid().nullable(),
  laborHours: z.string().nullable(),
  currentStateId: z.string().uuid(),
  currentState: z.enum(FLEET_FAILURE_STATES),
  notes: z.string().nullable(),
  // odometerHours/odometerKm/meterReadingMissingReason (MR-4/MR-5, Slice C) —
  // per-falla meter readings + the "pending workshop review" marker.
  // Non-null meterReadingMissingReason mirrors fuel_load's own field of the
  // same name/semantics (D3).
  odometerHours: z.string().nullable(),
  odometerKm: z.string().nullable(),
  meterReadingMissingReason: z.string().nullable(),
  createdAt: z.string().datetime().or(z.date()),
  updatedAt: z.string().datetime().or(z.date()),
  attachments: z.array(FleetFailureAttachmentResponseSchema),
  // locx-voz-multi-intencion (D11) — forward link to the dispatching
  // voice_intake row (source='voz' only). Optional: absent on every other row.
  voiceIntakeId: z.string().uuid().nullable().optional(),
  // Only populated by GET /servicios/fleet-failures (list) — denormalized from
  // the parent report + its asset (2026-07-21, fleet-wide report view) so a
  // grid can show a human vehicle label without a second round-trip per row.
  // Absent (undefined) on detail/create/update responses.
  assetId: z.string().uuid().optional(),
  assetSerialNumber: z.string().optional(),
  reportedAt: z.string().datetime().or(z.date()).optional(),
});
export type FleetFailureResponse = z.infer<typeof FleetFailureResponseSchema>;

export const FleetFailureDetailResponseSchema = FleetFailureResponseSchema.extend({
  availableTransitions: z.array(FleetFailureTransitionSchema.shape.toState),
});
export type FleetFailureDetailResponse = z.infer<typeof FleetFailureDetailResponseSchema>;

// GET /servicios/fleet-failure-reports/:id — the report's own detail
// (availableTransitions) PLUS its fallas embedded, same posture as
// RecruitmentRequestWithCandidaciesResponse embedding candidacies.
export const FleetFailureReportWithFailuresResponseSchema = FleetFailureReportDetailResponseSchema.extend({
  failures: z.array(FleetFailureResponseSchema),
});
export type FleetFailureReportWithFailuresResponse = z.infer<typeof FleetFailureReportWithFailuresResponseSchema>;

// ---------------------------------------------------------------------------
// failure_type / external_provider — small read-only catalogs (design.md §1).
// No admin CRUD contracts in this PR — pickers read via a simple list route
// backed directly by the repository (mirrors AssetTypeRepository.listActive
// consumption pattern), no dedicated admin ABM UI requested for PR1.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// GET /servicios/fleet-failures/dashboard (PR1.5 — design.md §7bis) — KPIs by
// state + breakdown by maintenanceKind, TENANT-WIDE (no per-user/role scoping
// yet — see design.md §7bis "Scoping por rol" for why that is deliberately
// deferred, NOT an oversight).
// ---------------------------------------------------------------------------

export const FleetFailureCountsByStateSchema = z.object({
  EMITIDO: z.number().int().nonnegative(),
  ASIGNADO: z.number().int().nonnegative(),
  ESPERA_REPUESTO: z.number().int().nonnegative(),
  RESUELTO: z.number().int().nonnegative(),
});
export type FleetFailureCountsByState = z.infer<typeof FleetFailureCountsByStateSchema>;

// 'unspecified' buckets fleet_failure rows with maintenanceKind = NULL — same
// naming convention as contracts-rrhh's genderDistribution 'unspecified' bucket.
export const FleetFailureCountsByMaintenanceKindSchema = z.object({
  corrective: z.number().int().nonnegative(),
  preventive: z.number().int().nonnegative(),
  unspecified: z.number().int().nonnegative(),
});
export type FleetFailureCountsByMaintenanceKind = z.infer<typeof FleetFailureCountsByMaintenanceKindSchema>;

export const FleetFailureDashboardResponseSchema = z.object({
  countsByState: FleetFailureCountsByStateSchema,
  totalCount: z.number().int().nonnegative(),
  byMaintenanceKind: FleetFailureCountsByMaintenanceKindSchema,
});
export type FleetFailureDashboardResponse = z.infer<typeof FleetFailureDashboardResponseSchema>;

export const FailureTypeResponseSchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid().nullable(),
  code: z.string(),
  label: z.string(),
  active: z.boolean(),
  createdAt: z.string().datetime().or(z.date()),
});
export type FailureTypeResponse = z.infer<typeof FailureTypeResponseSchema>;

// ---------------------------------------------------------------------------
// failure_type admin contracts — mirrors AssetType admin contracts exactly
// (configurable-engine.ts) — company_id/active never in request bodies (come
// from TenantContext / DB default); code is immutable after creation.
// ---------------------------------------------------------------------------

export const FailureTypeCreateSchema = z
  .object({
    code: z.string().min(1, 'code is required').max(128, 'code must be at most 128 characters'),
    label: z.string().min(1, 'label is required').max(256, 'label must be at most 256 characters'),
  })
  .strict();
export type FailureTypeCreate = z.infer<typeof FailureTypeCreateSchema>;

export const FailureTypeUpdateSchema = z
  .object({
    label: z.string().min(1, 'label is required').max(256, 'label must be at most 256 characters'),
  })
  .strict();
export type FailureTypeUpdate = z.infer<typeof FailureTypeUpdateSchema>;

/** FailureTypeResponseSchema is already the admin shape (companyId/active/createdAt included). */
export const FailureTypeAdminListResponseSchema = z.object({
  items: z.array(FailureTypeResponseSchema),
  totalCount: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
  totalPages: z.number().int().nonnegative(),
});
export type FailureTypeAdminListResponse = z.infer<typeof FailureTypeAdminListResponseSchema>;

export const ExternalProviderResponseSchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid().nullable(),
  name: z.string(),
  active: z.boolean(),
  createdAt: z.string().datetime().or(z.date()),
});
export type ExternalProviderResponse = z.infer<typeof ExternalProviderResponseSchema>;
